import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Warehouse, ArrowCircleDown, QrCode, Scales, WarningCircle, CheckCircle, X,
} from "@phosphor-icons/react";

export default function DisposalCenter() {
  const [checkins, setCheckins] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ vehicle_id: "", staff_id: "", collected_weight_kg: 0 });
  const [recvForm, setRecvForm] = useState({ qr_code: "", weight_kg: 0, manual: false });

  const load = async () => {
    const [c, v, s] = await Promise.all([
      api.get("/disposal/checkins"), api.get("/vehicles"), api.get("/staff"),
    ]);
    setCheckins(c.data); setVehicles(v.data); setStaff(s.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selected) { setReceipts([]); return; }
    api.get(`/disposal/receipts?checkin_id=${selected.id}`).then(r => setReceipts(r.data));
  }, [selected]);

  const createCi = async (e) => {
    e.preventDefault();
    try {
      await api.post("/disposal/checkin", {
        vehicle_id: form.vehicle_id,
        staff_id: form.staff_id || null,
        collected_weight_kg: Number(form.collected_weight_kg),
      });
      toast.success("Vehicle checked in");
      setOpenNew(false);
      setForm({ vehicle_id: "", staff_id: "", collected_weight_kg: 0 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const receive = async (e) => {
    e.preventDefault();
    try {
      await api.post("/disposal/receive", {
        checkin_id: selected.id,
        qr_code: recvForm.qr_code,
        weight_kg: Number(recvForm.weight_kg),
        manual: recvForm.manual,
      });
      toast.success("Received");
      setRecvForm({ qr_code: "", weight_kg: 0, manual: false });
      // reload checkin
      const r = await api.get("/disposal/checkins");
      setCheckins(r.data);
      const updated = r.data.find(x => x.id === selected.id);
      if (updated) setSelected(updated);
      const rec = await api.get(`/disposal/receipts?checkin_id=${selected.id}`);
      setReceipts(rec.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reconcile = async () => {
    if (!selected) return;
    try {
      const r = await api.post("/disposal/reconcile", { checkin_id: selected.id });
      toast.success(r.data.mismatch ? `Reconciled — MISMATCH ${r.data.diff_pct}%` : "Reconciled — OK");
      load();
      setSelected(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const vn = (id) => vehicles.find(v => v.id === id)?.vehicle_no || "—";
  const sn = (id) => staff.find(s => s.id === id)?.name || "—";

  return (
    <AppShell title="Disposal Center" subtitle="Module 10"
      actions={
        <button data-testid="btn-new-checkin" onClick={() => setOpenNew(true)}
                className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
          <ArrowCircleDown size={14} weight="bold"/>Vehicle check-in
        </button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E5E2D9] rounded-md">
          <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center gap-2">
            <Warehouse size={16} color="#2D5A27" weight="fill"/>
            <div className="font-heading">Check-ins</div>
          </div>
          <div className="divide-y divide-[#E5E2D9] max-h-[600px] overflow-y-auto" data-testid="checkins-list">
            {checkins.length === 0 && <div className="p-6 text-center text-sm text-[#8A8780]">No check-ins yet.</div>}
            {checkins.map(c => (
              <button key={c.id} onClick={() => setSelected(c)}
                      data-testid={`checkin-${c.id}`}
                      className={`w-full text-left px-5 py-3 hover:bg-[#F7F6F2] transition-colors ${selected?.id === c.id ? "bg-[#D4E0D1]" : ""}`}>
                <div className="flex items-center gap-2">
                  <div className="font-mono text-sm">{vn(c.vehicle_id)}</div>
                  <div className="flex-1"/>
                  {c.status === "open"
                    ? <span className="text-[10px] uppercase tracking-[0.18em] bg-[#F9E4B7] text-[#7A5A11] rounded px-2 py-0.5">Open</span>
                    : c.mismatch
                      ? <span className="text-[10px] uppercase tracking-[0.18em] bg-[#F4C7B9] text-[#7A2E18] rounded px-2 py-0.5">Mismatch</span>
                      : <span className="text-[10px] uppercase tracking-[0.18em] bg-[#D4E0D1] text-[#1A3317] rounded px-2 py-0.5">OK</span>}
                </div>
                <div className="text-xs text-[#5C5A55] mt-1">{sn(c.staff_id)} · collected {c.collected_weight_kg}kg · received {c.received_weight_kg || 0}kg</div>
                <div className="text-[10px] font-mono text-[#8A8780] mt-0.5">{new Date(c.arrival_time).toLocaleString()}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3">
          {!selected && (
            <div className="bg-white border border-[#E5E2D9] rounded-md p-16 text-center text-sm text-[#8A8780]">
              Select a check-in to process QR re-scans and reconciliation.
            </div>
          )}
          {selected && (
            <div className="space-y-6">
              <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Check-in</div>
                    <div className="font-heading text-2xl">{vn(selected.vehicle_id)}</div>
                    <div className="text-xs text-[#5C5A55]">{sn(selected.staff_id)} · arrived {new Date(selected.arrival_time).toLocaleString()}</div>
                  </div>
                  {selected.status === "open" && (
                    <button onClick={reconcile} data-testid="btn-reconcile"
                            className="h-10 px-4 bg-[#C25D38] hover:bg-[#A74E2F] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
                      <Scales size={14} weight="bold"/>Reconcile & close
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-4 mt-6">
                  <Stat label="Collected" value={`${selected.collected_weight_kg} kg`}/>
                  <Stat label="Received" value={`${selected.received_weight_kg || 0} kg`}/>
                  <Stat label="Bags" value={selected.received_bag_count || 0}/>
                  <Stat label="Diff" value={selected.diff_kg != null ? `${selected.diff_kg} kg` : "—"} danger={selected.mismatch}/>
                </div>
                {selected.mismatch && (
                  <div className="mt-4 p-3 bg-[#F4C7B9]/40 border border-[#C25D38]/30 rounded-md text-sm text-[#7A2E18] flex items-center gap-2">
                    <WarningCircle size={16} weight="fill"/>
                    Mismatch detected — {selected.diff_pct}% deviation. Alert raised.
                  </div>
                )}
              </div>

              {selected.status === "open" && (
                <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
                  <div className="flex items-center gap-2 mb-4"><QrCode size={18} color="#2D5A27" weight="fill"/><div className="font-heading text-lg">Receive QR bag</div></div>
                  <form onSubmit={receive} className="grid grid-cols-4 gap-3" data-testid="receive-form">
                    <div className="col-span-2">
                      <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">QR code</label>
                      <input data-testid="receive-qr" required value={recvForm.qr_code}
                             onChange={e => setRecvForm(f=>({...f, qr_code: e.target.value}))}
                             placeholder="RSG-..." className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Weight (kg)</label>
                      <input type="number" step="any" min="0" required value={recvForm.weight_kg}
                             onChange={e => setRecvForm(f=>({...f, weight_kg: e.target.value}))}
                             className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
                    </div>
                    <div className="flex flex-col justify-end">
                      <label className="inline-flex items-center gap-2 text-xs mb-2">
                        <input type="checkbox" checked={recvForm.manual}
                               onChange={e => setRecvForm(f=>({...f, manual: e.target.checked}))}
                               className="accent-[#2D5A27]"/>
                        Manual entry (flag)
                      </label>
                      <button data-testid="receive-submit" type="submit"
                              className="h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-1">
                        <CheckCircle size={14} weight="bold"/>Receive
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="bg-white border border-[#E5E2D9] rounded-md">
                <div className="px-6 py-4 border-b border-[#E5E2D9] font-heading">Receipts ({receipts.length})</div>
                <table className="w-full text-sm" data-testid="receipts-table">
                  <thead>
                    <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                      <th className="px-6 py-3">Time</th><th className="px-6 py-3">QR code</th>
                      <th className="px-6 py-3">Weight</th><th className="px-6 py-3">Manual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receipts.length === 0 && <tr><td colSpan={4} className="px-6 py-6 text-center text-sm text-[#8A8780]">No receipts.</td></tr>}
                    {receipts.map(r => (
                      <tr key={r.id}>
                        <td className="px-6 py-3 font-mono text-xs">{new Date(r.timestamp).toLocaleTimeString()}</td>
                        <td className="px-6 py-3 font-mono">{r.qr_code}</td>
                        <td className="px-6 py-3 font-mono">{r.weight_kg} kg</td>
                        <td className="px-6 py-3">{r.manual ? <span className="text-[#C25D38] text-xs">Manual</span> : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {openNew && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpenNew(false)}>
          <div className="bg-white rounded-md w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading text-lg">Vehicle check-in</div>
              <button onClick={() => setOpenNew(false)} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
            </div>
            <form onSubmit={createCi} className="space-y-4" data-testid="checkin-form">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Vehicle</label>
                <select data-testid="checkin-vehicle" required value={form.vehicle_id}
                        onChange={e => setForm(f=>({...f, vehicle_id: e.target.value}))}
                        className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
                  <option value="">Select</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_no}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Staff (optional)</label>
                <select value={form.staff_id}
                        onChange={e => setForm(f=>({...f, staff_id: e.target.value}))}
                        className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
                  <option value="">—</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Collected weight (kg)</label>
                <input data-testid="checkin-weight" type="number" step="any" min="0" required
                       value={form.collected_weight_kg}
                       onChange={e => setForm(f=>({...f, collected_weight_kg: e.target.value}))}
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
              </div>
              <button data-testid="checkin-submit" type="submit"
                      className="w-full h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
                Check in
              </button>
            </form>
          </div>
        </div>
      )}
    </AppShell>
  );
}

const Stat = ({ label, value, danger }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">{label}</div>
    <div className={`font-heading text-2xl mt-1 ${danger ? "text-[#C25D38]" : "text-[#1A1A1A]"}`}>{value}</div>
  </div>
);
